import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { LocalizationConfig } from '../../localization/LocalizationConfig';
import { ApiRoutes } from './ApiRoutes';
import { AuthenticationService } from './auth-service.service';
import { Gt2ApiService } from './gt2-api.service';

interface PendingLanguage {
  locale?: string;
  employeeUuid?: string;
}

/**
 * Saves the language chosen with the FR/EN switch of the toolbar on the
 * employee record of the authenticated user, so the server writes to that
 * user (notification emails) in the language of the interface.
 *
 * API used, unchanged: GET /v1/locals (code → uuid) then
 * PATCH /v1/employees/{uuid} with `{ employee_local: { uuid } }` (every role).
 *
 * The switch reloads the page: the choice is noted in localStorage
 * (`remember`), then saved by the page that comes back (`savePending`).
 * Never blocking: the calls skip the HTTP interceptors (a failure must not
 * send the user to an error page), fail silently, and are tried again at the
 * next page load.
 */
@Injectable({ providedIn: 'root' })
export class UserLanguageService {
  private static readonly STORAGE_KEY = 'GT3_PENDING_SERVER_LANGUAGE';

  /** Without interceptors. */
  private readonly http: HttpClient;
  private saving = false;

  constructor(
    handler: HttpBackend,
    private api: Gt2ApiService,
    private auth: AuthenticationService,
  ) {
    this.http = new HttpClient(handler);
  }

  public remember(locale: string, employeeUuid: string | undefined): void {
    if (!locale || !employeeUuid) {
      return;
    }
    try {
      localStorage.setItem(
        UserLanguageService.STORAGE_KEY,
        JSON.stringify({ locale, employeeUuid }),
      );
    } catch (error) {
      /* storage refused: the language of the interface still changes */
    }
  }

  public savePending(employeeUuid: string | undefined): void {
    if (this.saving || !employeeUuid) {
      return;
    }

    const pending = this.readPending();
    if (!pending?.locale || !pending?.employeeUuid) {
      return;
    }
    // Noted by another user of this browser: not ours to save.
    if (pending.employeeUuid !== employeeUuid) {
      this.clear();
      return;
    }

    const token = this.auth.getToken();
    if (!token) {
      return;
    }

    const locale = pending.locale;
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + token,
      Accept: 'application/json',
      'Accept-Language': LocalizationConfig.getCurrentLanguage().locale,
      'app-version': environment.appVersion ? environment.appVersion : '',
      'App-Tenant': localStorage.getItem('PREFIX') || '',
    });

    this.saving = true;
    this.http
      .get<any>(this.api.createUrl(ApiRoutes.GT2_LOCALE), { headers })
      .pipe(
        switchMap((response: any) => {
          const local = (response?.data ?? []).find(
            (item: any) => item?.code === locale,
          );
          if (!local?.uuid) {
            // Language unknown to the server: nothing to save, nothing to retry.
            return of(false);
          }
          return this.http.patch<any>(
            this.api.createUrl(ApiRoutes.UPDATE_EMPLOYEE) +
              encodeURIComponent(employeeUuid),
            { employee_local: { uuid: local.uuid } },
            { headers },
          );
        }),
        // null = the call failed: the choice is kept for the next page load.
        catchError(() => of(null)),
      )
      .subscribe({
        next: (result: any) => {
          this.saving = false;
          if (result !== null) {
            this.clear();
          }
        },
      });
  }

  private readPending(): PendingLanguage | null {
    try {
      return JSON.parse(
        localStorage.getItem(UserLanguageService.STORAGE_KEY) || 'null',
      );
    } catch (error) {
      return null;
    }
  }

  private clear(): void {
    try {
      localStorage.removeItem(UserLanguageService.STORAGE_KEY);
    } catch (error) {
      /* nothing to do */
    }
  }
}
